import { Component, ElementRef, OnInit, ViewChild } from "@angular/core";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { MapService } from "src/services/map.service";
import { ModelAndDealerService } from "src/services/model-and-dealer.service";
import {
  ChannelEnum,
  DealerViewModel,
  GeoLocationModel,
  ModelViewModel,
  SearchDealerRequestModel,
} from "src/domain/client";
import { StockholmCenterCoordinates } from "src/domain/constants";
import { BrandService } from "src/services/brand.service";
import { SpaceValidator } from "src/domain/validators/noSpaceValidator";
import { ChannelRequestService } from "src/services/channel-request.service";
import { ExpandComponent } from "../expand/expand.component";
import { FilterDealerPipe } from "src/pipes/filter-dealer.pipe";
import { ILocation } from "src/interfaces/interfaces";
import { URLService } from "src/services/url.service";
import { DealerSelectionMethod } from "src/domain/trackingEnums";
import { nonNullish } from "src/guards";
import { ErrorReporterService } from "src/services/error-reporter.service";

@Component({
  selector: "vw-select-dealer",
  templateUrl: "./select-dealer.component.html",
})
export class SelectDealerComponent implements OnInit {
  public locations?: ILocation[];
  public selectedLocation?: ILocation;
  public clientLocation?: ILocation;

  @ViewChild(ExpandComponent) expand?: ExpandComponent;
  @ViewChild("search") public searchElementRef?: ElementRef;

  vehicleByModel = false;
  dealersInMap: DealerViewModel[] = [];
  allDealersInMap: DealerViewModel[] = [];
  saving = false;

  constructor(
    public mapService: MapService,
    public modelAndDealerService: ModelAndDealerService,
    public channelRequestService: ChannelRequestService,
    public urlService: URLService,
    private brandService: BrandService,
    private errorReporter: ErrorReporterService,
    private filterDealerPipe: FilterDealerPipe,
  ) {
    // needed for the callback pipe
    this.filterDealer = this.filterDealer.bind(this);
  }

  regNrForm = new FormGroup({
    regNr: new FormControl("", [
      Validators.required,
      SpaceValidator.cannotContainSpace,
    ]),
  });

  changeDealerForm = new FormGroup({
    dealer: new FormControl(""),
    selectedDealerId: new FormControl<number>(NaN),
  });

  ngOnInit(): void {
    this.initFormSubscribers();
    this.modelAndDealerService.getModelGroups();
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition((position: any) => {
        this.clientLocation = {
          longitude: position.coords.longitude,
          latitude: position.coords.latitude,
        };
      });
    } else {
      this.clientLocation = {
        longitude: StockholmCenterCoordinates.lng,
        latitude: StockholmCenterCoordinates.lat,
      };
    }
    if (
      !!this.mapService.geoLocationModel &&
      this.mapService.geoLocationModel.latitude != null &&
      this.mapService.geoLocationModel.longitude != null
    ) {
      this.selectedLocation = {
        latitude: this.mapService.geoLocationModel.latitude,
        longitude: this.mapService.geoLocationModel.longitude,
      };
    }
  }

  filterDealer(dealer: DealerViewModel): boolean {
    const formDealer = this.changeDealerForm.value.dealer ?? "";
    return this.filterDealerPipe.transform(formDealer, dealer);
  }

  selectDealer(dealer: DealerViewModel): void {
    this.changeDealerForm.patchValue(
      {
        selectedDealerId: dealer.dealerId,
        dealer: dealer.name,
      },
      { emitEvent: false },
    );
    this.selectedLocation =
      dealer.latitude != null && dealer.longitude != null
        ? {
            latitude: dealer.latitude,
            longitude: dealer.longitude,
          }
        : undefined;
  }

  toggleSearch(): void {
    if (!this.vehicleByModel) {
      this.regNrForm.reset();
    } else {
      this.modelAndDealerService.modelForm.reset();
    }

    this.vehicleByModel = !this.vehicleByModel;
  }

  save(event: MouseEvent): void {
    event.preventDefault();

    const regNr = this.regNrForm.controls.regNr.valid
      ? this.regNrForm.value.regNr
      : undefined;

    const dealer = this.modelAndDealerService.dealers.find(
      (_) => _.dealerId === this.changeDealerForm.value.selectedDealerId,
    );

    const model = this.modelAndDealerService.models.find(
      (_) => _.name === this.modelAndDealerService.modelForm.value.model,
    );

    if (!regNr && !dealer && !model) {
      this.expand?.toggleOpen();
      return;
    }

    this.saving = true;

    this.updateChannelRequest(
      regNr,
      dealer,
      DealerSelectionMethod.FROM_MAP,
      model,
      false,
    );
  }

  resetAll(): void {
    this.updateChannelRequest(
      undefined,
      undefined,
      undefined,
      undefined,
      true,
      true,
    );
  }

  resetVehicle(): void {
    const dealer = this.modelAndDealerService.selectedDealer;
    this.updateChannelRequest(
      undefined,
      dealer,
      undefined,
      undefined,
      true,
      true,
    );
  }

  resetDealer(): void {
    const shoppingCart =
      this.channelRequestService.channelRequest?.shoppingCart;
    const regNr = shoppingCart?.vehicle?.licensePlate;

    let model: ModelViewModel | undefined;

    if (shoppingCart?.vehicle) {
      model = new ModelViewModel({
        year: shoppingCart.vehicle?.year,
        modelCode: shoppingCart.vehicle?.modelCode,
      });
    }

    this.updateChannelRequest(regNr, null, null, model, true, true);
  }

  open(): void {
    this.getDealersToShowInGoogleMaps();

    if (this.modelAndDealerService.selectedDealer) {
      this.changeDealerForm.patchValue(
        {
          selectedDealerId: this.modelAndDealerService.selectedDealer.dealerId,
        },
        { emitEvent: false },
      );
    }

    this.expand?.toggleOpen();
  }

  onLocationSelect(location: ILocation): void {
    this.selectedLocation = location;
    this.sortByPosition(this.selectedLocation);
    const dealer = this.dealersInMap.find(
      (d) =>
        JSON.stringify({
          longitude: d.longitude,
          latitude: d.latitude,
        }) === JSON.stringify(location),
    );
    if (dealer != null) this.selectDealer(dealer);
  }
  onLocationSearch(location: ILocation): void {
    this.sortByPosition(location);
    this.selectedLocation = location;
  }

  private sortByPosition(position: ILocation): void {
    this.allDealersInMap.sort(
      (w1, w2) =>
        (w1.latitude != null && w1.longitude != null
          ? this.dist(position, {
              longitude: w1.longitude,
              latitude: w1.latitude,
            })
          : Infinity) -
        (w2.latitude != null && w2.longitude != null
          ? this.dist(position, {
              longitude: w2.longitude,
              latitude: w2.latitude,
            })
          : Infinity),
    );
    this.dealersInMap = this.allDealersInMap.slice(0, 5);
  }

  private dist(from: ILocation, to: ILocation): number {
    const p = 0.017453292519943295; // Math.PI / 180
    const c = Math.cos;
    const a =
      0.5 -
      c((to.latitude - from.latitude) * p) / 2 +
      (c(from.latitude * p) *
        c(to.latitude * p) *
        (1 - c((to.longitude - from.longitude) * p))) /
        2;
    return (12742 * Math.asin(Math.sqrt(a))) / 100; // 2 * R; R = 6371 km
  }

  private getDealerLocations(): void {
    this.allDealersInMap = this.modelAndDealerService.dealers;
    this.locations = this.allDealersInMap
      .map((d: DealerViewModel) => {
        return d.latitude != null && d.longitude != null
          ? {
              longitude: d.longitude,
              latitude: d.latitude,
            }
          : null;
      })
      .filter(nonNullish);
  }

  private updateChannelRequest(
    regNr?: string | null,
    dealer?: DealerViewModel | null,
    dealerSelectionMethod?: DealerSelectionMethod | null,
    model?: ModelViewModel | null,
    showCarAndDealerMenuToast = true,
    reset = false,
  ): void {
    this.saving = true;

    this.channelRequestService
      .updateChannelRequest(
        regNr,
        dealer,
        dealerSelectionMethod,
        model,
        showCarAndDealerMenuToast,
        reset,
      )
      .then(() => {
        this.expand?.toggleOpen();
      })
      .catch(this.errorReporter.handle)
      .finally(() => {
        this.saving = false;
      });
  }

  private initFormSubscribers(): void {
    // reset selected dealer when the text box in the typeahead changes
    this.changeDealerForm.get("dealer")?.valueChanges.subscribe(() => {
      this.changeDealerForm.patchValue({
        selectedDealerId: undefined,
      });
    });
  }

  private getDealersToShowInGoogleMaps(): void {
    this.mapService
      .getCurrentPosition()
      .then((geoLocation: GeoLocationModel) => {
        this.getNearestDealers(geoLocation);
        this.getDealerLocations();
      })
      .catch(() => {
        this.dealersInMap = this.modelAndDealerService.dealers;
      });
  }

  private getNearestDealers(geoLocation: GeoLocationModel): void {
    let requestModel: SearchDealerRequestModel;
    if (this.modelAndDealerService.presetDealers?.length > 0) {
      requestModel = new SearchDealerRequestModel({
        brandId: this.brandService.brandId,
        channel: ChannelEnum.TBO,
      });
    } else {
      requestModel = new SearchDealerRequestModel({
        brandId: this.brandService.brandId,
        geoLocation,
        limit: 5,
        channel: ChannelEnum.TBO,
      });
    }

    this.modelAndDealerService
      .getDealers(requestModel, false)
      .then((dealers) => {
        this.dealersInMap = dealers;
        if (!!dealers.length && !this.modelAndDealerService.selectedDealer) {
          this.selectDealer(dealers[0]);
        } else if (this.modelAndDealerService.presetDealers?.length > 0) {
          this.selectDealer(dealers[0]);
        }
      })
      .catch(this.errorReporter.handle);
  }
}
